import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { authErrorMessage, useAuth } from "@/auth/AuthProvider";
import { Copy } from "@/copy/copy";
import { FieldError } from "@/components/fields";

const schema = z.object({
  email: z.string().trim().email("That doesn’t look like an email address."),
  password: z.string().min(6, "Choose a password with at least 6 characters."),
});
type Values = z.infer<typeof schema>;

/**
 * Sign-up on the household step, where v47 asks for the email ("About you"): signed out, the step's
 * Continue creates the account (or, "Sign in instead", signs in) before moving on. Every later step
 * needs an account.
 */
export function useAccountFields() {
  const { signUp, signIn } = useAuth();
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  /** Checks the fields and signs up (or in): the email used, or undefined when it didn't work. */
  const submit = async (): Promise<string | undefined> => {
    setError(null);
    if (!(await form.trigger())) return undefined;
    const { email, password } = form.getValues();
    try {
      if (mode === "signup") await signUp(email.trim(), password);
      else await signIn(email.trim(), password);
      return email.trim();
    } catch (e) {
      setError(authErrorMessage(e));
      return undefined;
    }
  };

  return { form, mode, setMode, error, setError, submit };
}

export function AccountFields({ account }: { account: ReturnType<typeof useAccountFields> }) {
  const { form, mode, setMode, error, setError } = account;
  const errs = form.formState.errors;
  return (
    <div data-testid="onb-account">
      <div className="field">
        <label htmlFor="acct-email">Email</label>
        <input
          id="acct-email"
          type="email"
          autoComplete="email"
          {...form.register("email")}
          data-testid="onb-email"
        />
        <p className="hint">We&rsquo;ll send your plan here. No marketing unless you ask for it.</p>
        <FieldError error={errs.email?.message} />
      </div>
      <div className="field">
        <label htmlFor="acct-password">Password</label>
        <input
          id="acct-password"
          type="password"
          autoComplete={mode === "signup" ? "new-password" : "current-password"}
          {...form.register("password")}
          data-testid="onb-password"
        />
        <FieldError error={errs.password?.message} />
      </div>
      {error && (
        <p className="wp-form-err" role="alert">
          {error}
        </p>
      )}
      {mode === "signup" && <Copy k="auth.signup.legal" as="p" className="hint" />}
      <p className="hint" style={{ marginTop: 12 }}>
        {mode === "signup" ? "Already have an account? " : "New here? "}
        <button
          type="button"
          className="linkbtn"
          onClick={() => {
            setError(null);
            setMode(mode === "signup" ? "signin" : "signup");
          }}
          data-testid="onb-account-toggle"
        >
          {mode === "signup" ? "Sign in instead" : "Create an account instead"}
        </button>
      </p>
    </div>
  );
}
